import React from 'react';

const EDGE_THRESHOLD_PX = 2;

/*
 * A scroll pane with bottom-lock ("follow") semantics for streaming content:
 * while `follow` is on and the user has not intervened, the pane stays pinned
 * to its tail as content grows — watching the output unfold like a movie.
 * Scrolling up releases the lock; scrolling back to the bottom (or the parent
 * re-engaging `follow`, e.g. the chat's own bottom lock) re-arms it.
 */
class FollowPane extends React.Component {
  paneRef = React.createRef();

  locked = true;
  lastScrollTop = 0;
  pinFrame = null;
  releaseSuppressFrame = null;
  scrollSuppressed = false;
  resizeObserver = null;
  mutationObserver = null;

  componentDidMount() {
    const pane = this.paneRef.current;

    if (!pane) {
      return;
    }

    this.locked = Boolean(this.props.follow);
    this.lastScrollTop = pane.scrollTop;

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.handleLayoutChange);
      this.observeChildren();
    }

    if (typeof MutationObserver === 'function') {
      this.mutationObserver = new MutationObserver(() => {
        this.observeChildren();
        this.handleLayoutChange();
      });
      this.mutationObserver.observe(pane, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.handleLayoutChange();
  }

  componentDidUpdate(prevProps) {
    if (this.props.follow && !prevProps.follow) {
      this.locked = true;
      this.pinToBottom();
    }
    this.handleLayoutChange();
  }

  componentWillUnmount() {
    if (this.pinFrame) {
      window.cancelAnimationFrame(this.pinFrame);
      this.pinFrame = null;
    }

    if (this.releaseSuppressFrame) {
      window.cancelAnimationFrame(this.releaseSuppressFrame);
      this.releaseSuppressFrame = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  observeChildren = () => {
    const pane = this.paneRef.current;

    if (!pane || !this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.resizeObserver.observe(pane);
    Array.from(pane.children).forEach(child => this.resizeObserver.observe(child));
  };

  isAtBottom = () => {
    const pane = this.paneRef.current;

    if (!pane) {
      return false;
    }

    return pane.scrollHeight - pane.clientHeight - pane.scrollTop <= EDGE_THRESHOLD_PX;
  };

  pinToBottom = () => {
    const pane = this.paneRef.current;

    if (!pane) {
      return;
    }

    if (this.releaseSuppressFrame) {
      window.cancelAnimationFrame(this.releaseSuppressFrame);
      this.releaseSuppressFrame = null;
    }

    this.scrollSuppressed = true;
    pane.scrollTop = Math.max(0, pane.scrollHeight - pane.clientHeight);
    this.lastScrollTop = pane.scrollTop;

    /*
     * Scroll events from programmatic writes may fire async: keep them
     * suppressed until the next frame so they never read as user intent.
     */
    this.releaseSuppressFrame = window.requestAnimationFrame(() => {
      this.scrollSuppressed = false;
      this.releaseSuppressFrame = null;
    });
  };

  handleLayoutChange = () => {
    if (!this.props.follow || !this.locked) {
      return;
    }

    if (this.pinFrame) {
      return;
    }

    this.pinToBottom();
    this.pinFrame = window.requestAnimationFrame(() => {
      this.pinFrame = null;

      if (this.props.follow && this.locked) {
        this.pinToBottom();
      }
    });
  };

  recordUserDelta = deltaY => {
    if (!Number.isFinite(deltaY) || deltaY === 0) {
      return;
    }

    if (deltaY < 0) {
      this.locked = false;
    } else if (this.isAtBottom()) {
      this.locked = true;
    }
  };

  handleWheel = event => {
    this.recordUserDelta(event.deltaY);
  };

  handleTouchStart = event => {
    if (event.touches.length > 0) {
      this.lastTouchY = event.touches[0].clientY;
    }
  };

  handleTouchMove = event => {
    if (event.touches.length === 0) {
      return;
    }

    const currentY = event.touches[0].clientY;

    if (this.lastTouchY == null) {
      this.lastTouchY = currentY;
      return;
    }

    const deltaY = this.lastTouchY - currentY;
    this.lastTouchY = currentY;
    this.recordUserDelta(deltaY);
  };

  handleScroll = () => {
    const pane = this.paneRef.current;

    if (!pane) {
      return;
    }

    const scrollTop = pane.scrollTop;

    if (this.scrollSuppressed) {
      this.lastScrollTop = scrollTop;
      return;
    }

    const deltaY = scrollTop - this.lastScrollTop;
    this.lastScrollTop = scrollTop;
    this.recordUserDelta(deltaY);
  };

  render() {
    const { className, style, children } = this.props;

    return (
      <div
        ref={this.paneRef}
        className={className}
        style={{ overflowY: 'auto', ...style }}
        onScroll={this.handleScroll}
        onWheel={this.handleWheel}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
      >
        {children}
      </div>
    );
  }
}

export default FollowPane;
export { FollowPane };
